import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
// import DynamicTable from "../../../../components/Dynamic-table";
import DynamicTable from "../../../../components/lab-dynamic-table";
import {
  getDefaultRecordDeleteTooltip,
  getDefaultRecordEditTooltip,
  isDefaultLabRecord,
} from "../../utils/defaultRecordActions";

import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

type AgeType = {
  yy: number;
  mm: number;
  dd: number;
};

type PatientFormType = {
  patientTypeName: string;
  patientTypeDescription: string;
  ageFrom: AgeType;
  ageTo: AgeType;
};

const PatientType: React.FC<any> = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [patientData, setPatientData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<PatientFormType>({
    patientTypeName: "",
    patientTypeDescription: "",
    ageFrom: { yy: 0, mm: 0, dd: 0 },
    ageTo: { yy: 0, mm: 0, dd: 0 },
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/patient-type/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: searchInput,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setPatientData(response.data?.data || []);
      setTotal(response.data?.totalCount || 0);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error(error?.response.data.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: string, rowData: any) => {
    setEditData(id);
    setFormData({
      patientTypeName: rowData?.patientTypeName || "",
      patientTypeDescription: rowData?.patientTypeDescription || "",
      ageFrom: rowData?.ageFrom || { yy: 0, mm: 0, dd: 0 },
      ageTo: rowData?.ageTo || { yy: 0, mm: 0, dd: 0 },
    });
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleSubmit = async () => {
    if (editData) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/patient-type/updateById/${editData}`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData({
            patientTypeName: "",
            patientTypeDescription: "",
            ageFrom: { yy: 0, mm: 0, dd: 0 },
            ageTo: { yy: 0, mm: 0, dd: 0 },
          });
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAB_URL}/patient-type/addPatientType`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData({
            patientTypeName: "",
            patientTypeDescription: "",
            ageFrom: { yy: 0, mm: 0, dd: 0 },
            ageTo: { yy: 0, mm: 0, dd: 0 },
          });
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/patient-type/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchData();
      toast.success(response.data.message);
      setFormData({
        patientTypeName: "",
        patientTypeDescription: "",
        ageFrom: { yy: 0, mm: 0, dd: 0 },
        ageTo: { yy: 0, mm: 0, dd: 0 },
      });
      setEditData(null);
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1rem",
              sm: "1.25rem",
              md: "1.5rem",
            },
          }}
        >
          Patient Type
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search Patient Type..."
            variant="outlined"
            value={searchInput}
            // className="search-field"
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Patient Type" : "Add New Patient Type"}
            </Typography>
            <TextField
              size="small"
              label="Patient Type Name"
              name="patientTypeName"
              // className="search-field"
              className="custom-input"
              value={formData.patientTypeName}
              fullWidth
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  patientTypeName: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              size="small"
              label="Description"
              // className="search-field"
              className="custom-input"
              name="patientTypeDescription"
              value={formData.patientTypeDescription}
              fullWidth
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  patientTypeDescription: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />

            {/* Age From */}
            <Typography sx={{ mb: 1 }}>Age From</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  // className="search-field"
                  className="custom-input"
                  label="Year"
                  type="string"
                  value={formData.ageFrom.yy || "0"}
                  fullWidth
                  inputProps={{ maxLength: 4 }}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                    setFormData((prev) => ({
                      ...prev,
                      ageFrom: { ...prev.ageFrom, yy: val ? parseInt(val) : 0 },
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  // className="search-field"
                  className="custom-input"
                  label="Month"
                  type="string"
                  value={formData.ageFrom.mm?.toString() ?? "0"}
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, "").slice(0, 2);
                    if (val === "") {
                      setFormData((prev) => ({
                        ...prev,
                        ageFrom: { ...prev.ageFrom, mm: 0 },
                      }));
                      return;
                    }
                    const num = parseInt(val, 10);
                    if (num >= 1 && num <= 11) {
                      setFormData((prev) => ({
                        ...prev,
                        ageFrom: { ...prev.ageFrom, mm: num },
                      }));
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  // className="search-field"
                  className="custom-input"
                  label="Day"
                  type="string"
                  value={formData.ageFrom.dd.toString() ?? "0"}
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                    if (val === "") {
                      setFormData((prev) => ({
                        ...prev,
                        ageFrom: { ...prev.ageFrom, dd: 0 },
                      }));
                      return;
                    }
                    const num = parseInt(val, 10);
                    if (num >= 1 && num <= 30) {
                      setFormData((prev) => ({
                        ...prev,
                        ageFrom: { ...prev.ageFrom, dd: num },
                      }));
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Age To */}
            <Typography sx={{ mb: 1 }}>Age To</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  label="Year"
                  // className="search-field"
                  className="custom-input"
                  type="string"
                  value={formData.ageTo.yy || "0"}
                  fullWidth
                  inputProps={{ maxLength: 4 }}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 2);

                    setFormData((prev) => ({
                      ...prev,
                      ageTo: { ...prev.ageTo, yy: val ? parseInt(val) : 0 },
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  label="Month"
                  // className="search-field"
                  className="custom-input"
                  type="string"
                  value={formData.ageTo.mm.toString() ?? "0"}
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                    if (val === "") {
                      setFormData((prev) => ({
                        ...prev,
                        ageTo: { ...prev.ageTo, mm: 0 },
                      }));
                      return;
                    }

                    const num = parseInt(val, 10);
                    if (num >= 1 && num <= 11) {
                      setFormData((prev) => ({
                        ...prev,
                        ageTo: { ...prev.ageTo, mm: num },
                      }));
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  label="Day"
                  // className="search-field"
                  className="custom-input"
                  type="string"
                  value={formData.ageTo.dd.toString() ?? "0"}
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                    if (val === "") {
                      setFormData((prev) => ({
                        ...prev,
                        ageTo: { ...prev.ageTo, mm: 0 },
                      }));
                      return;
                    }

                    const num = parseInt(val, 10);
                    if (num >= 1 && num <= 30) {
                      setFormData((prev) => ({
                        ...prev,
                        ageTo: { ...prev.ageTo, dd: num },
                      }));
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              className="search-field"
              disabled={!formData.patientTypeName}
            >
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8} md={8}>
          <DynamicTable
            headers={[
              "SN",
              "Patient Name",
              "Description",
              "From(YY-MM-DD)",
              "To(YY-MM-DD)",
              "Actions",
            ]}
            data={patientData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item?.patientTypeName || "",
              item?.patientTypeDescription || "",
              `${item?.ageFrom?.yy}-${item?.ageFrom?.mm}-${item?.ageFrom?.dd}`,
              `${item?.ageTo?.yy}-${item?.ageTo?.mm}-${item?.ageTo?.dd}`,
            ])}
            loading={false}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(
                    patientData[rowIndex]?._id,
                    patientData[rowIndex]
                  ),
                disabled: (rowIndex) => isDefaultLabRecord(patientData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordEditTooltip(patientData[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(patientData[rowIndex]?._id),
                disabled: (rowIndex) => isDefaultLabRecord(patientData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordDeleteTooltip(patientData[rowIndex]),
              },
            ]}
            height={"60vh"}
          />
          {patientData?.length === 0 && (
            <h1
              style={{
                color: "gray",
                marginTop: "8px",
                textAlign: "center",
                fontSize: "20px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              No data found
            </h1>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default PatientType;
